import React from 'react';
import { useHistory } from 'react-router-dom';

const isSameOriginReferrer = (referrer: string): boolean => {
  if (!referrer) return false;
  try {
    return new URL(referrer).origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * Diz se existe uma "página anterior do portal" para onde voltar.
 *
 * São dois caminhos possíveis de chegada, e cada um tem seu sinal:
 *
 * - **Navegando dentro da SPA** (clicou num link quebrado a partir de outra
 *   página do portal): o react-router registra a transição, e
 *   `history.action` fica `PUSH`/`REPLACE`. Voltar leva para o portal.
 * - **Load direto** (colou a URL, veio de um e-mail, F5): `history.action` é
 *   `POP`. Aí o único indício é o `document.referrer` — se ele apontar para a
 *   mesma origem, o usuário veio de outra página do portal.
 *
 * Fora esses dois casos o botão simplesmente não aparece: voltar mandaria o
 * cidadão para fora do site (ou para lugar nenhum).
 *
 * O cálculo mora num efeito e o estado inicial é `false` de propósito — o SSR
 * não tem `document.referrer` nem histórico, então servidor e primeiro render
 * do cliente coincidem e a hidratação não diverge.
 */
export function useCanGoBack(): boolean {
  const history = useHistory();
  const [canGoBack, setCanGoBack] = React.useState(false);

  React.useEffect(() => {
    const cameFromSpaNavigation =
      history.action === 'PUSH' || history.action === 'REPLACE';
    setCanGoBack(
      cameFromSpaNavigation || isSameOriginReferrer(document.referrer),
    );
  }, [history.action]);

  return canGoBack;
}

export default useCanGoBack;
